import integrations from './Modules/integrations';
import scheduler from './Modules/schedule';
import offerTemplates from './Modules/offer-template';
import scheduleAll from './Modules/schedule-all';
import contact from './Modules/contact';
import attachments from './Modules/attachments';
import offers from './Modules/offers';
import extAssessment from './Modules/ext-assessment';
import backgroundCheck from './Modules/background-check';
import workingHours from './Modules/working-hours';
import knockout from './Modules/knockout';
import requisitions from './Modules/requisitions';
import fraudDetector from './Modules/fraud-detector';
import availabilityScheduler from './Modules/availability-scheduler';

const ERROR_MODULES = {
  integrations,
  scheduler,
  offerTemplates,
  scheduleAll,
  contact,
  attachments,
  offers,
  extAssessment,
  backgroundCheck,
  workingHours,
  knockout,
  requisitions,
  fraudDetector,
  availabilityScheduler,
};

const DEFAULT_ERROR_MESSAGE =
  'Something went wrong. Please try again later or contact support@kula.ai';

/**
 * Extracts the most specific error information from the result object.
 * Priority:
 * 1. result.errors[0].reason (if result.errors[0] is an object with 'reason')
 * 2. result.errors[0] (if it exists)
 * 3. result.error[0] (if it exists)
 * 4. result[0] (if it exists)
 * Returns undefined if none are found.
 */
function getErrorKey(result) {
  // Check safely for result.errors[0]
  const firstErrorInErrors = result?.errors?.[0];

  // Check if it's an object and prioritize the 'reason' property
  if (typeof firstErrorInErrors === 'object' && firstErrorInErrors !== null) {
    // If 'reason' exists (is not null/undefined), use it. Otherwise, use the object itself.
    return firstErrorInErrors.reason ?? firstErrorInErrors;
  }

  // If firstErrorInErrors wasn't an object or didn't exist,
  // try result.errors[0] directly (could be a string, number, etc.)
  // then fallback to result.error[0], then result[0].
  const errorKey = firstErrorInErrors ?? result?.error?.[0] ?? result?.[0];

  // If errorKey is a string and contains a colon, extract only the error code part
  // (e.g., "err_zinc_invitation_already_exists: Additional message" -> "err_zinc_invitation_already_exists")
  if (typeof errorKey === 'string' && errorKey.includes(':')) {
    return errorKey.split(':')[0].trim();
  }

  if (
    typeof errorKey === 'string' &&
    !errorKey.startsWith('err_') &&
    result?.error_code
  ) {
    return result.error_code;
  }

  return errorKey;
}

const getErrorMessage = (result, type, customErrorHandler = null) => {
  const moduleErrors = ERROR_MODULES[type];
  if (!moduleErrors) return DEFAULT_ERROR_MESSAGE;

  const errorKey = getErrorKey(result);

  if (customErrorHandler) {
    const customMessage = customErrorHandler(errorKey, result);
    if (customMessage) {
      return customMessage;
    }
  }

  const errorValue = moduleErrors?.[errorKey];
  return errorValue?.message || DEFAULT_ERROR_MESSAGE;
};

const getErrorMessages = (error, type) => {
  const moduleErrors = ERROR_MODULES[type];
  if (!moduleErrors) return [DEFAULT_ERROR_MESSAGE];
  const errors = error?.errors; // this will have actual server error code
  const errorValues = errors?.map(
    (errorKey) => moduleErrors[errorKey]?.message || DEFAULT_ERROR_MESSAGE
  );
  return errorValues || [DEFAULT_ERROR_MESSAGE];
};

const handleKomboApiError = (errorKey, result) => {
  if (errorKey === 'err_kombo_api_failed') {
    const message = result?.details;
    if (message) {
      try {
        const parsedMessage = JSON.parse(message);
        const errorMessage =
          parsedMessage?.error?.message ||
          parsedMessage?.error?.message?.remote_error?.error ||
          parsedMessage?.error?.message?.remote_error ||
          'Validation failed';
        return errorMessage;
      } catch {
        return 'Something went wrong. Please try again later or contact support@kula.ai';
      }
    }
  }
  return null;
};

export { getErrorMessage, getErrorMessages, handleKomboApiError };
